import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

const IDDSI_LEVELS = {
  foods: [
    { level: 3, name: 'Liquidised', emoji: '🥣', color: '#F4A460', description: 'Smooth like a thick soup' },
    { level: 4, name: 'Pureed', emoji: '🥄', color: '#E07020', description: 'Smooth like yoghurt' },
    { level: 5, name: 'Minced & Moist', emoji: '🍲', color: '#C92D2D', description: 'Tiny soft pieces' },
    { level: 6, name: 'Soft & Bite-Sized', emoji: '🍽️', color: '#2D7DC9', description: 'Soft, small bites' },
    { level: 7, name: 'Regular', emoji: '🥗', color: '#4A4A4A', description: 'Normal food' },
  ],
  drinks: [
    { level: 0, name: 'Thin', emoji: '💧', color: '#87CEEB', description: 'Like water' },
    { level: 1, name: 'Slightly Thick', emoji: '🥛', color: '#B8B8B8', description: 'Thicker than water' },
    { level: 2, name: 'Mildly Thick', emoji: '🍯', color: '#FFB6C1', description: 'Like a milkshake' },
    { level: 3, name: 'Moderately Thick', emoji: '🥫', color: '#F4A460', description: 'Like honey' },
    { level: 4, name: 'Extremely Thick', emoji: '🍮', color: '#E07020', description: 'Like pudding' },
  ]
};

// Profile selector component
function ProfileSelector({ profiles, selectedProfile, onSelect }) {
  if (!profiles.length) return null;
  
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '24px',
      padding: '16px 20px',
      boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
      marginBottom: '20px',
    }}>
      <div style={{ fontSize: '13px', fontWeight: '600', color: '#777', marginBottom: '10px', textTransform: 'uppercase' }}>
        Who are you checking for?
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <button
          onClick={() => onSelect(null)}
          style={{
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: '600',
            border: '2px solid',
            borderColor: !selectedProfile ? '#FF6B35' : '#E8E8E8',
            borderRadius: '12px',
            backgroundColor: !selectedProfile ? '#FFF5F0' : 'white',
            color: !selectedProfile ? '#FF6B35' : '#666',
            cursor: 'pointer',
          }}
        >
          Manual Selection
        </button>
        {profiles.map(p => (
          <button
            key={p.id}
            onClick={() => onSelect(p)}
            style={{
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '600',
              border: '2px solid',
              borderColor: selectedProfile?.id === p.id ? '#FF6B35' : '#E8E8E8',
              borderRadius: '12px',
              backgroundColor: selectedProfile?.id === p.id ? '#FFF5F0' : 'white',
              color: selectedProfile?.id === p.id ? '#FF6B35' : '#333',
              cursor: 'pointer',
            }}
          >
            <span style={{ marginRight: '6px' }}>👤</span>
            {p.name}
            {p.roomNumber && <span style={{ color: '#999', marginLeft: '6px', fontSize: '12px' }}>{p.roomNumber}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

const SYSTEM_PROMPT = `You are an expert IDDSI (International Dysphagia Diet Standardisation Initiative) advisor helping carers check if foods and drinks are suitable for people with swallowing difficulties.

IDDSI FRAMEWORK OVERVIEW:
- Level 0 (Thin): Water-like liquids
- Level 1 (Slightly Thick): Thicker than water, flows quickly
- Level 2 (Mildly Thick): Flows off spoon slowly
- Level 3 (Liquidised/Moderately Thick): Smooth, no lumps, can be drunk from cup or eaten with spoon
- Level 4 (Pureed/Extremely Thick): Smooth, holds shape on spoon, no chewing needed
- Level 5 (Minced & Moist): Soft lumps ≤4mm (adult) or ≤2mm (child), minimal chewing, moist with thick sauce
- Level 6 (Soft & Bite-Sized): Tender pieces ≤1.5cm, can be mashed with fork, chewing required
- Level 7 (Regular): Normal food / Level 7 Easy to Chew: Soft tender foods, normal size

KEY SAFETY CONSIDERATIONS:
- Mixed consistencies (foods that change texture or release liquid) are risky at lower levels
- Skins on foods (peas, grapes, tomatoes) can separate and cause choking
- Stringy/fibrous foods need extra attention
- Hard, crunchy, or crumbly foods are problematic at lower levels
- Sticky foods can be difficult to clear from the mouth

When analysing foods/drinks, you MUST respond with valid JSON only, no other text. Use this exact structure:

{
  "items": [
    {
      "name": "Item name",
      "status": "ok|modify|avoid|check",
      "simple": "1-3 word status",
      "detailed": "1-2 sentence explanation with practical advice",
      "full": "Detailed guidance including preparation steps, testing methods, common mistakes, and alternatives"
    }
  ],
  "summary": "One sentence overall summary"
}

Status meanings:
- "ok": Safe and suitable as typically prepared
- "modify": Can be made suitable with specific modifications
- "avoid": Not recommended, suggest alternatives
- "check": Depends on individual prescription or needs verification

Be practical, friendly, and helpful. Use plain English. Focus on actionable advice.`;

function App() {
  const [selectedType, setSelectedType] = useState('foods');
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [query, setQuery] = useState('');
  const [detailLevel, setDetailLevel] = useState('detailed');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [expandedTests, setExpandedTests] = useState({});
  const [restrictions, setRestrictions] = useState([]);
  const [newRestriction, setNewRestriction] = useState('');
  const [activeTab, setActiveTab] = useState('foods');
  const [showScanner, setShowScanner] = useState(false);
  const [scannedItems, setScannedItems] = useState([]);
  const [scannerError, setScannerError] = useState(null);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [disclaimerPending, setDisclaimerPending] = useState(false);
  const scannerRef = useRef(null);
  const scannerDivId = 'barcode-scanner';

  const levels = IDDSI_LEVELS[selectedType];
  const selectedLevelData = levels.find(l => l.level === selectedLevel);

  // Fetch profiles on mount
  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const res = await fetch('/api/profiles', { headers: { 'X-API-Key': 'demo-api-key-12345' } });
      const data = await res.json();
      setProfiles(data.data || []);
    } catch (err) {
      console.error('Failed to fetch profiles:', err);
    }
  };

  // When profile is selected, set levels from profile
  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
    if (profile) {
      setSelectedLevel(selectedType === 'foods' ? profile.foodLevel : profile.drinkLevel);
    } else {
      setSelectedLevel(null);
    }
    setResults(null);
    setError(null);
  };

  // Update level when type changes with profile selected
  useEffect(() => {
    if (selectedProfile) {
      setSelectedLevel(selectedType === 'foods' ? selectedProfile.foodLevel : selectedProfile.drinkLevel);
    }
  }, [selectedType, selectedProfile]);

  const handleCheck = async () => {
    if (!selectedLevel === null || !query.trim()) return;
    
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await fetch('/api/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: selectedType,
          level: selectedLevel,
          levelName: selectedLevelData?.name,
          query: query.trim(),
          profileId: selectedProfile?.id,
          restrictions: restrictions,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check - please try again');
      }

      const data = await response.json();
      setResults(data);
      setDisclaimerPending(true);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDisclaimer = () => {
    setDisclaimerPending(false);
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'ok': return { bg: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)', border: '#66BB6A', text: '#2E7D32', icon: '✅', label: 'Safe' };
      case 'modify': return { bg: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)', border: '#FFB74D', text: '#E65100', icon: '⚠️', label: 'Modify' };
      case 'avoid': return { bg: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)', border: '#EF5350', text: '#C62828', icon: '❌', label: 'Avoid' };
      case 'check': return { bg: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)', border: '#42A5F5', text: '#1565C0', icon: '❓', label: 'Check' };
      default: return { bg: '#F5F5F5', border: '#9E9E9E', text: '#424242', icon: '•', label: '' };
    }
  };

  const handleReset = () => {
    setResults(null);
    setQuery('');
    setExpandedTests({});
    setScannedItems([]);
  };

  const toggleTest = (idx) => {
    setExpandedTests(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const addRestriction = () => {
    const val = newRestriction.trim();
    if (val && !restrictions.includes(val)) {
      setRestrictions([...restrictions, val]);
      setNewRestriction('');
    }
  };

  const removeRestriction = (r) => {
    setRestrictions(restrictions.filter(x => x !== r));
  };

  const startScanner = async () => {
    setScannerError(null);
    setShowScanner(true);
    // Wait for DOM element to be rendered
    setTimeout(async () => {
      try {
        const html5Qrcode = new Html5Qrcode(scannerDivId);
        scannerRef.current = html5Qrcode;
        await html5Qrcode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 150 } },
          async (decodedText) => {
            // Avoid duplicate scans
            if (scannedItems.some(item => item.barcode === decodedText)) return;
            setScannerLoading(true);
            setScannerError(null);
            try {
              const res = await fetch(`/api/barcode/${decodedText}`);
              if (!res.ok) {
                setScannerError(`Product not found for barcode: ${decodedText}`);
                setScannerLoading(false);
                return;
              }
              const product = await res.json();
              setScannedItems(prev => {
                if (prev.some(item => item.barcode === product.barcode)) return prev;
                return [...prev, product];
              });
              setScannerError(null);
            } catch {
              setScannerError('Failed to look up barcode');
            }
            setScannerLoading(false);
          },
          () => {} // ignore scan failures (no barcode in frame)
        );
      } catch (err) {
        setScannerError('Could not access camera. Please allow camera permissions.');
        setShowScanner(false);
      }
    }, 100);
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch {}
      scannerRef.current = null;
    }
    setShowScanner(false);
  };

  const removeScannedItem = (barcode) => {
    setScannedItems(scannedItems.filter(item => item.barcode !== barcode));
  };

  const handleCheckScanned = async () => {
    if (!scannedItems.length || selectedLevel === null) return;

    const queryParts = scannedItems.map(item => {
      let desc = item.name;
      if (item.ingredients) desc += ` (ingredients: ${item.ingredients})`;
      return desc;
    });

    setLoading(true);
    setError(null);
    setResults(null);
    await stopScanner();

    try {
      const response = await fetch('/api/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: selectedType,
          level: selectedLevel,
          levelName: selectedLevelData?.name,
          query: queryParts.join(', '),
          profileId: selectedProfile?.id,
          restrictions: restrictions,
        }),
      });

      if (!response.ok) throw new Error('Failed to check - please try again');
      const data = await response.json();
      setResults(data);
      setDisclaimerPending(true);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Clean up scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #FFF9F0 0%, #FFF5E6 50%, #FFEFD6 100%)',
      fontFamily: '"Nunito", "Quicksand", "Segoe UI", sans-serif',
    }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #FF9A56 0%, #FF6B35 100%)',
        padding: '20px 24px',
        color: 'white',
        borderRadius: '0 0 32px 32px',
        boxShadow: '0 4px 20px rgba(255,107,53,0.3)',
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: '28px', 
            fontWeight: '800',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            🍽️ IDDSI Check
          </h1>
          <p style={{ margin: '8px 0 0', opacity: 0.95, fontSize: '15px', fontWeight: '500' }}>
            Check if food or drinks are safe and suitable
          </p>
        </div>
      </header>

      <main style={{ maxWidth: '600px', margin: '0 auto', padding: '24px 16px' }}>
        
        {/* Type selector */}
        <div style={{
          display: 'flex',
          gap: '4px',
          padding: '6px',
          backgroundColor: 'white',
          borderRadius: '50px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          marginBottom: '20px',
        }}>
          {[
            { key: 'foods', label: 'Food' },
            { key: 'allergies', label: 'Allergies' },
            { key: 'drinks', label: 'Drinks' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                if (tab.key !== 'allergies') {
                  setSelectedType(tab.key);
                  setSelectedLevel(null);
                  setResults(null);
                  setError(null);
                }
              }}
              style={{
                flex: 1,
                padding: '14px 12px',
                fontSize: '15px',
                fontWeight: '700',
                border: 'none',
                borderRadius: '50px',
                background: activeTab === tab.key
                  ? 'linear-gradient(135deg, #FF9A56 0%, #FF6B35 100%)'
                  : 'transparent',
                color: activeTab === tab.key ? 'white' : '#666',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {tab.key === 'allergies' && restrictions.length > 0
                ? `Allergies (${restrictions.length})`
                : tab.label}
            </button>
          ))}
        </div>

        {/* Allergies & Restrictions Panel */}
        {activeTab === 'allergies' && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '20px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            marginBottom: '20px',
          }}>
            <h2 style={{
              margin: '0 0 4px',
              fontSize: '18px',
              fontWeight: '700',
              color: '#333',
            }}>
              Allergies & Restrictions
            </h2>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#777' }}>
              Add any allergies, intolerances, E numbers, or dietary restrictions. These will be checked against all food and drink queries.
            </p>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                value={newRestriction}
                onChange={(e) => setNewRestriction(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addRestriction()}
                placeholder="e.g. Peanuts, E102, Gluten, Shellfish..."
                style={{
                  flex: 1,
                  padding: '14px 16px',
                  fontSize: '15px',
                  border: '2px solid #E8E8E8',
                  borderRadius: '14px',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#FF9A56'}
                onBlur={(e) => e.target.style.borderColor = '#E8E8E8'}
              />
              <button
                onClick={addRestriction}
                disabled={!newRestriction.trim()}
                style={{
                  padding: '14px 20px',
                  fontSize: '15px',
                  fontWeight: '700',
                  border: 'none',
                  borderRadius: '14px',
                  background: newRestriction.trim()
                    ? 'linear-gradient(135deg, #FF9A56 0%, #FF6B35 100%)'
                    : '#CCC',
                  color: 'white',
                  cursor: newRestriction.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Add
              </button>
            </div>

            {restrictions.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {restrictions.map(r => (
                  <span
                    key={r}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      backgroundColor: '#FFEBEE',
                      color: '#C62828',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: '1px solid #EF9A9A',
                    }}
                  >
                    {r}
                    <button
                      onClick={() => removeRestriction(r)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#C62828',
                        cursor: 'pointer',
                        padding: '0 0 0 4px',
                        fontSize: '16px',
                        fontWeight: '700',
                        lineHeight: 1,
                      }}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#999',
                fontSize: '14px',
                backgroundColor: '#FAFAFA',
                borderRadius: '14px',
              }}>
                No allergies or restrictions added yet
              </div>
            )}
          </div>
        )}

        {activeTab !== 'allergies' && (<>
        {/* Level cards */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '20px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          marginBottom: '20px',
        }}>
          <h2 style={{ 
            margin: '0 0 16px', 
            fontSize: '18px', 
            fontWeight: '700',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span style={{ 
              width: '28px', 
              height: '28px', 
              borderRadius: '50%', 
              background: 'linear-gradient(135deg, #FF9A56 0%, #FF6B35 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: '800',
            }}>1</span>
            Select level to check
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {levels.map(level => (
              <button
                key={level.level}
                onClick={() => { setSelectedLevel(level.level); setResults(null); setError(null); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 16px',
                  border: '2px solid',
                  borderColor: selectedLevel === level.level ? level.color : '#E8E8E8',
                  borderRadius: '16px',
                  backgroundColor: selectedLevel === level.level ? `${level.color}15` : 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: level.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}>
                  {level.emoji}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '700', color: '#333', fontSize: '15px' }}>
                    Level {level.level} · {level.name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#777', marginTop: '2px' }}>
                    {level.description}
                  </div>
                </div>
                {selectedLevel === level.level && (
                  <span style={{ fontSize: '20px' }}>✓</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Search input */}
        {selectedLevel !== null && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '20px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            marginBottom: '20px',
          }}>
            <h2 style={{ 
              margin: '0 0 12px', 
              fontSize: '18px', 
              fontWeight: '700',
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <span style={{ 
                width: '28px', 
                height: '28px', 
                borderRadius: '50%', 
                background: 'linear-gradient(135deg, #FF9A56 0%, #FF6B35 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '800',
              }}>2</span>
              What do you want to check?
            </h2>
            
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 14px',
              backgroundColor: `${selectedLevelData?.color}20`,
              borderRadius: '20px',
              marginBottom: '14px',
              fontSize: '14px',
              fontWeight: '600',
              color: selectedLevelData?.color,
            }}>
              {selectedLevelData?.emoji} Level {selectedLevel} · {selectedLevelData?.name}
            </div>

            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={selectedType === 'foods' 
                ? "Type a meal or food, e.g. 'Sunday roast' or 'fish fingers'" 
                : "Type a drink, e.g. 'orange juice' or 'tea with milk'"}
              style={{
                width: '100%',
                padding: '16px 18px',
                fontSize: '16px',
                border: '2px solid #E8E8E8',
                borderRadius: '16px',
                boxSizing: 'border-box',
                marginBottom: '14px',
                outline: 'none',
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
              onFocus={(e) => e.target.style.borderColor = '#FF9A56'}
              onBlur={(e) => e.target.style.borderColor = '#E8E8E8'}
            />
            
            <button
              onClick={handleCheck}
              disabled={loading || !query.trim()}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '17px',
                fontWeight: '700',
                background: loading || !query.trim() 
                  ? '#CCC' 
                  : 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: loading || !query.trim() ? 'not-allowed' : 'pointer',
                boxShadow: loading || !query.trim() ? 'none' : '0 4px 15px rgba(76,175,80,0.3)',
                transition: 'all 0.2s ease',
              }}
            >
              {loading ? '🔍 Checking...' : '✓ Check Now'}
            </button>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              margin: '18px 0',
            }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#E0E0E0' }} />
              <span style={{ fontSize: '13px', color: '#999', fontWeight: '600' }}>OR</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#E0E0E0' }} />
            </div>

            {/* Barcode Scanner */}
            <button
              onClick={showScanner ? stopScanner : startScanner}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                fontWeight: '700',
                background: showScanner
                  ? '#EF5350'
                  : 'linear-gradient(135deg, #7B1FA2 0%, #9C27B0 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                boxShadow: showScanner ? 'none' : '0 4px 15px rgba(156,39,176,0.3)',
                transition: 'all 0.2s ease',
              }}
            >
              {showScanner ? 'Close Scanner' : 'Scan Barcode'}
            </button>

            {scannerError && (
              <div style={{
                marginTop: '12px',
                padding: '10px 14px',
                backgroundColor: '#FFF3E0',
                borderRadius: '12px',
                fontSize: '13px',
                color: '#E65100',
                fontWeight: '600',
              }}>
                {scannerError}
              </div>
            )}

            {showScanner && (
              <div style={{ marginTop: '14px' }}>
                <div
                  id={scannerDivId}
                  style={{
                    width: '100%',
                    borderRadius: '14px',
                    overflow: 'hidden',
                  }}
                />
                {scannerLoading && (
                  <div style={{
                    marginTop: '10px',
                    textAlign: 'center',
                    fontSize: '14px',
                    color: '#666',
                    fontWeight: '600',
                  }}>
                    Looking up product...
                  </div>
                )}
              </div>
            )}

            {/* Scanned Items List */}
            {scannedItems.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#333',
                  marginBottom: '10px',
                }}>
                  Scanned Items ({scannedItems.length})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {scannedItems.map(item => (
                    <div
                      key={item.barcode}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 14px',
                        backgroundColor: '#F3E5F5',
                        borderRadius: '12px',
                        border: '1px solid #CE93D8',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                          {item.name}
                        </div>
                        {item.allergens.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#C62828', marginTop: '4px', fontWeight: '600' }}>
                            Allergens: {item.allergens.join(', ')}
                          </div>
                        )}
                        {item.additives.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#E65100', marginTop: '2px' }}>
                            Additives: {item.additives.join(', ')}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeScannedItem(item.barcode)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#999',
                          cursor: 'pointer',
                          fontSize: '18px',
                          fontWeight: '700',
                          padding: '4px 8px',
                        }}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleCheckScanned}
                  disabled={loading || selectedLevel === null}
                  style={{
                    width: '100%',
                    marginTop: '14px',
                    padding: '16px',
                    fontSize: '17px',
                    fontWeight: '700',
                    background: loading || selectedLevel === null
                      ? '#CCC'
                      : 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '16px',
                    cursor: loading || selectedLevel === null ? 'not-allowed' : 'pointer',
                    boxShadow: loading || selectedLevel === null ? 'none' : '0 4px 15px rgba(76,175,80,0.3)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {loading ? '🔍 Checking...' : `✓ Check ${scannedItems.length} Scanned Item${scannedItems.length > 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={{
            backgroundColor: '#FFEBEE',
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '20px',
            color: '#C62828',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            {error}
          </div>
        )}

        {/* Disclaimer acceptance before results */}
        {results && disclaimerPending && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '28px 24px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          }}>
            <h2 style={{
              margin: '0 0 16px',
              fontSize: '18px',
              fontWeight: '700',
              color: '#E65100',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <span style={{ fontSize: '22px' }}>⚠️</span>
              Before You View These Results
            </h2>
            <p style={{
              margin: '0 0 16px',
              fontSize: '15px',
              lineHeight: '1.6',
              color: '#333',
              fontWeight: '600',
            }}>
              This tool provides general guidance only. It is not a medical device.
            </p>
            <ul style={{
              margin: '0 0 20px',
              paddingLeft: '20px',
              fontSize: '14px',
              lineHeight: '1.8',
              color: '#555',
            }}>
              <li>Results are AI-generated and may not account for individual needs</li>
              <li>Always follow the person's care plan and SLT recommendations</li>
              <li><strong>If in doubt, do not serve</strong> — check with the care team first</li>
              <li>Texture and consistency can vary based on how food is prepared</li>
            </ul>
            <button
              onClick={handleAcceptDisclaimer}
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '17px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #FF9A56 0%, #FF6B35 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(255,107,53,0.3)',
                marginBottom: '16px',
              }}
            >
              I Understand — Show Results
            </button>
            <p style={{
              margin: 0,
              fontSize: '12px',
              lineHeight: '1.5',
              color: '#999',
              textAlign: 'center',
            }}>
              By viewing these results you confirm you understand this is guidance only and all decisions must be based on professional clinical assessment.
            </p>
          </div>
        )}

        {/* Results */}
        {results && !disclaimerPending && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '24px',
            padding: '20px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#333' }}>
                🔍 Results
              </h2>
              <div style={{ 
                display: 'flex', 
                gap: '4px', 
                backgroundColor: '#F5F5F5', 
                borderRadius: '20px', 
                padding: '4px' 
              }}>
                {[
                  { key: 'simple', label: 'Quick' },
                  { key: 'detailed', label: 'Helpful' },
                  { key: 'full', label: 'Full Guide' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setDetailLevel(key)}
                    style={{
                      padding: '8px 14px',
                      fontSize: '13px',
                      fontWeight: detailLevel === key ? '700' : '500',
                      backgroundColor: detailLevel === key ? 'white' : 'transparent',
                      border: 'none',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      color: detailLevel === key ? '#FF6B35' : '#666',
                      boxShadow: detailLevel === key ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary banner */}
            <div style={{
              padding: '14px 18px',
              background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
              borderRadius: '14px',
              marginBottom: '16px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#E65100',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <span style={{ fontSize: '20px' }}>💡</span>
              {results.summary}
            </div>

            {/* Result items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {results.items.map((item, idx) => {
                const config = getStatusConfig(item.status);
                return (
                  <div
                    key={idx}
                    style={{
                      padding: '16px',
                      background: config.bg,
                      borderRadius: '16px',
                      border: `2px solid ${config.border}`,
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px',
                      marginBottom: detailLevel === 'simple' ? 0 : '10px',
                    }}>
                      <span style={{ fontSize: '22px' }}>{config.icon}</span>
                      <strong style={{ color: config.text, fontSize: '16px', flex: 1 }}>
                        {item.name}
                      </strong>
                      <span style={{ 
                        padding: '6px 12px',
                        backgroundColor: config.border,
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '700',
                      }}>
                        {item.simple}
                      </span>
                    </div>
                    {detailLevel !== 'simple' && (
                      <div style={{
                        marginLeft: '32px',
                        color: '#444',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {detailLevel === 'detailed' ? item.detailed : item.full}
                      </div>
                    )}
                    {item.testMethod && (item.status === 'modify' || item.status === 'check') && (
                      <div style={{ marginLeft: '32px', marginTop: '10px' }}>
                        <button
                          onClick={() => toggleTest(idx)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 14px',
                            fontSize: '13px',
                            fontWeight: '600',
                            backgroundColor: 'rgba(255,255,255,0.7)',
                            border: `1px solid ${config.border}`,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            color: config.text,
                          }}
                        >
                          <span>{expandedTests[idx] ? '▾' : '▸'}</span>
                          How to test
                        </button>
                        {expandedTests[idx] && (
                          <div style={{
                            marginTop: '10px',
                            padding: '14px 16px',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            borderRadius: '12px',
                            border: '1px solid rgba(0,0,0,0.08)',
                            fontSize: '13px',
                            lineHeight: '1.7',
                            color: '#444',
                            whiteSpace: 'pre-wrap',
                          }}>
                            {item.testMethod}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Check another button */}
            <button
              onClick={handleReset}
              style={{
                width: '100%',
                marginTop: '20px',
                padding: '14px',
                fontSize: '15px',
                fontWeight: '600',
                background: 'transparent',
                color: '#FF6B35',
                border: '2px solid #FF6B35',
                borderRadius: '16px',
                cursor: 'pointer',
              }}
            >
              Check Something Else
            </button>
          </div>
        )}
        </>)}
      </main>

      {/* Disclaimer */}
      <div style={{
        maxWidth: '600px',
        margin: '24px auto 0',
        padding: '0 16px',
      }}>
        <div style={{
          backgroundColor: '#FFF3E0',
          border: '1px solid #FFB74D',
          borderRadius: '16px',
          padding: '16px 20px',
        }}>
          <div style={{
            fontWeight: '700',
            fontSize: '14px',
            color: '#E65100',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            Important Notice
          </div>
          <p style={{
            margin: 0,
            fontSize: '13px',
            lineHeight: '1.6',
            color: '#555',
          }}>
            This tool provides <strong>general guidance only</strong> and is not a medical device. It is intended to support — not replace — proper training in supporting people with eating, drinking and swallowing difficulties, and clinical assessment. All dietary decisions must be based on an up-to-date assessment by a qualified Speech and Language Therapist (SLT). Always follow your organisation's policies and the person's individualised care plan. <strong>If in doubt, do not serve — check with the care team first.</strong>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        padding: '20px 16px 32px',
        textAlign: 'center',
        color: '#999',
        fontSize: '12px',
        lineHeight: '1.6',
      }}>
        <div>Based on the <a href="https://iddsi.org" target="_blank" rel="noopener noreferrer" style={{ color: '#999', textDecoration: 'underline' }}>IDDSI Framework</a></div>
        <div style={{ marginTop: '4px' }}>Not a medical device. For general guidance only. Always follow the person's care plan.</div>
      </footer>
    </div>
  );
}

export default App;
